import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import { HOURS_DECIMALS } from '../../EmploymentHours.consts';
import { BalanceValue, WabDot } from '../../EmploymentHours.styles';
import { formatHours } from '../../EmploymentHours.utils';

import type { WabSettings } from '../../EmploymentHours.types';
import type { BalanceCellProps } from './BalanceCell.types';

/** Below half of the last displayed decimal the value rounds to zero. */
const roundsToZero = (value: number) =>
  Math.abs(value) < 0.5 / 10 ** HOURS_DECIMALS;

/**
 * WAB zone, from `GridColumns.js:85-95`: warning when the percentage sits between the two levels
 * inclusive, alert when it exceeds the alert level, nothing below the warning level.
 */
const getWabTone = (
  percentage?: number,
  settings?: WabSettings | null
): 'warning' | 'error' | undefined => {
  // Dojo used a truthiness check, so a percentage of 0 shows no marker. Kept deliberately.
  if (!percentage || !settings) {
    return undefined;
  }

  if (percentage > settings.alertLevel) {
    return 'error';
  }

  return percentage >= settings.warningLevel ? 'warning' : undefined;
};

/**
 * The hours-balance cell. Ported from `EmployeeWorkAgreementsGridColumns.js:49-115`.
 *
 * A negative raw balance means the employee worked MORE than contracted. With the plus/minus org
 * setting on, the displayed sign is inverted so "too much" reads as a green `+`. Dojo compared the
 * formatted string against the literal `'0.00'`, which breaks in a comma-decimal locale; the zero
 * test here is numeric.
 */
const BalanceCell = ({
  value,
  showPlusMinus,
  isCumulative,
  wabPercentage,
  wabSettings,
  isTotals
}: BalanceCellProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'employmentHours' });

  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }

  const signed = showPlusMinus ? -value : value;
  const isZero = roundsToZero(signed);

  let tone: 'success' | 'error' | undefined;
  let text = formatHours(signed);
  let tooltip = '';

  if (!isZero) {
    if (value < 0) {
      tooltip = t('workedTooMuch');

      if (showPlusMinus) {
        tone = 'success';
        text = `+${text}`;
      }
    } else {
      tooltip = t('workedTooLittle');

      if (showPlusMinus) {
        tone = 'error';
      }
    }
  }

  const wabTone =
    isCumulative && !isTotals
      ? getWabTone(wabPercentage, wabSettings)
      : undefined;

  if (wabTone && wabPercentage) {
    tooltip = t('wabWorkedTooMuch', { percentage: Math.round(wabPercentage) });
  }

  const content = (
    <span>
      <BalanceValue tone={tone}>{text}</BalanceValue>
      {/* decorative: the tooltip on the wrapper carries the accessible name */}
      {wabTone && (
        <WabDot tone={wabTone} aria-hidden data-testid="wab-marker" />
      )}
    </span>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

export default BalanceCell;
